import React from 'react';

/**
 * Template "factory" — l'usine (PO / Dev / QA).
 *
 * Deux usages :
 *
 *  1. `variant: "curve"` → la courbe en J, dessinée ici (`<JCurve />`).
 *
 *  2. sans variant → la simulation « LES BASSINS », qui n'est PAS rendue par ce
 *     template : elle tourne dans `<UsineFrame />`, une iframe persistante
 *     montée par `ConferencePage` et posée par-dessus cette slide. Ce que l'on
 *     voit ici sert alors de **repli visuel** si l'iframe ne charge pas — d'où
 *     un rendu volontairement autonome, sans dépendance et sans réseau.
 *     Le repli reste piloté par `slide.tanks` / `slide.flow` / `slide.overflow`.
 *
 * Champs de slide :
 *  - stage    : label du tour (ex. "Tour 2 — le débordement")
 *  - title    : titre de la slide
 *  - subtitle : sous-titre
 *  - variant  : "tanks" (défaut) | "curve" (la courbe en J)
 *  - tanks    : [{ label, fill (0-100), speed, overflow }]
 *  - flow     : { label, value (0-100), trend: "up" | "down" | "flat" }
 *  - points   : légende typée sous l'animation (animation de frappe standard)
 */

const TREND_COLORS = {
    up: { text: 'text-emerald-300', bar: 'from-emerald-500 to-emerald-300', sign: '▲' },
    down: { text: 'text-red-300', bar: 'from-red-500 to-orange-400', sign: '▼' },
    flat: { text: 'text-blue-300', bar: 'from-blue-500 to-cyan-400', sign: '▬' }
};

/** Rendu provisoire — à remplacer par l'animation fournie. */
const FactoryStage = ({ tanks = [], overflow = false }) => (
    <div className="flex items-end justify-center gap-16 h-full">
        {tanks.map((tank, i) => {
            const isOverflowing = tank.overflow;
            return (
                <div key={i} className="flex flex-col items-center gap-4">
                    <div className="relative w-40 h-64 rounded-b-xl border-2 border-blue-500/40 bg-slate-900/60 overflow-visible">
                        {isOverflowing && (
                            <div className="absolute -top-6 left-0 right-0 h-6 bg-gradient-to-t from-red-500/70 to-transparent rounded-t-xl animate-pulse" />
                        )}
                        <div
                            className={`absolute bottom-0 left-0 right-0 rounded-b-lg transition-all duration-1000 ${isOverflowing
                                ? 'bg-gradient-to-t from-red-600/80 to-orange-400/60'
                                : 'bg-gradient-to-t from-blue-600/70 to-cyan-400/50'
                                }`}
                            style={{ height: `${Math.min(100, tank.fill ?? 0)}%` }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-4xl font-black text-white/80 drop-shadow">{tank.fill ?? 0}%</span>
                        </div>
                    </div>
                    <span className="text-2xl font-mono uppercase tracking-widest text-blue-200/80">{tank.label}</span>
                    {tank.speed && (
                        <span className="text-lg font-mono text-cyan-300/70">{tank.speed}</span>
                    )}
                </div>
            );
        })}
        {overflow && (
            <div className="absolute bottom-4 right-6 text-xl font-mono text-red-300/80 animate-pulse">
                débordement
            </div>
        )}
    </div>
);

/** La courbe en J — le débit baisse avant de remonter. */
const JCurve = () => (
    <svg viewBox="0 0 600 260" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
        <defs>
            <linearGradient id="jcurve-stroke" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#f87171" />
                <stop offset="55%" stopColor="#fbbf24" />
                <stop offset="100%" stopColor="#22d3ee" />
            </linearGradient>
        </defs>

        <line x1="60" y1="220" x2="570" y2="220" stroke="rgba(59,130,246,0.35)" strokeWidth="2" />
        <line x1="60" y1="20" x2="60" y2="220" stroke="rgba(59,130,246,0.35)" strokeWidth="2" />
        <line
            x1="60" y1="90" x2="570" y2="90"
            stroke="rgba(148,163,184,0.35)" strokeWidth="2" strokeDasharray="6 8"
        />
        <text x="500" y="80" fill="rgba(148,163,184,0.7)" fontSize="16" fontFamily="monospace">
            débit d'avant
        </text>

        <path
            d="M60,90 C150,110 200,180 260,190 C330,200 400,140 470,90 C510,62 540,45 570,35"
            fill="none"
            stroke="url(#jcurve-stroke)"
            strokeWidth="5"
            strokeLinecap="round"
        />

        <circle cx="260" cy="190" r="8" fill="#fbbf24" />
        <text x="200" y="228" fill="#fbbf24" fontSize="17" fontFamily="monospace">
            le creux
        </text>

        <text x="20" y="30" fill="rgba(148,163,184,0.7)" fontSize="16" fontFamily="monospace">
            débit
        </text>
        <text x="520" y="245" fill="rgba(148,163,184,0.7)" fontSize="16" fontFamily="monospace">
            temps
        </text>
    </svg>
);

const FactorySlide = ({ slide, slideKey, showFallback = true, currentPointIndex, typingStates }) => {
    const trend = TREND_COLORS[slide.flow?.trend] || TREND_COLORS.flat;
    // La courbe en J est toujours dessinée ici ; les cuves ne le sont que si la
    // simulation en iframe n'a pas démarré. Sinon on les apercevait sous elle
    // pendant le fondu, avec un tout autre style que la vraie machine.
    const courbe = slide.variant === 'curve';
    const afficherScene = courbe || showFallback;

    // La simulation va recouvrir toute la slide : ne rien dessiner dessous.
    // Un fond sobre suffit, le temps que l'iframe finisse son fondu.
    if (!afficherScene) {
        return <div key={slideKey} className="h-full bg-slate-950" />;
    }

    return (
        <div key={slideKey} className="h-full p-12 flex flex-col relative">
            <div className="relative z-10 flex flex-col h-full min-h-0">
                <div className="text-center flex-shrink-0">
                    {slide.stage && (
                        <p className="text-xl font-mono uppercase tracking-[0.3em] text-blue-300/50 mb-3 animate-fade-in-up">
                            {slide.stage}
                        </p>
                    )}
                    <h2 className="text-5xl pb-2 font-bold bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent animate-fade-in-up stagger-1">
                        {slide.title}
                    </h2>
                    {slide.subtitle && (
                        <p className="text-2xl text-blue-200/60 mt-2 animate-fade-in-up stagger-2">
                            {slide.subtitle}
                        </p>
                    )}
                </div>

                <div className="flex-1 min-h-0 my-8 relative rounded-2xl border border-blue-500/20 bg-slate-950/40 p-8">
                    {courbe
                        ? <JCurve />
                        : <FactoryStage tanks={slide.tanks} overflow={slide.overflow} />}
                </div>

                <div className="flex-shrink-0 flex items-center gap-8">
                    {slide.flow && (
                        <div className="flex-shrink-0 w-72">
                            <div className="flex items-baseline justify-between mb-2">
                                <span className="text-xl font-mono uppercase tracking-widest text-blue-200/70">
                                    {slide.flow.label || 'Débit en sortie'}
                                </span>
                                <span className={`text-2xl font-black ${trend.text}`}>{trend.sign}</span>
                            </div>
                            <div className="h-4 rounded-full bg-slate-800 overflow-hidden">
                                <div
                                    className={`h-full rounded-full bg-gradient-to-r ${trend.bar} transition-all duration-1000`}
                                    style={{ width: `${Math.min(100, slide.flow.value ?? 0)}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {slide.points?.length > 0 && (
                        <div className="flex-1 space-y-2">
                            {slide.points.map((point, i) => (
                                <p
                                    key={i}
                                    className={`text-2xl text-slate-300 font-mono leading-snug ${i <= currentPointIndex ? 'opacity-100' : 'opacity-0'}`}
                                >
                                    {i < currentPointIndex ? point :
                                        i === currentPointIndex ? (
                                            <>
                                                {typingStates?.[i] || ''}
                                                {typingStates?.[i] !== point && <span className="typing-cursor"></span>}
                                            </>
                                        ) : ''}
                                </p>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FactorySlide;
